import { shuffle } from '../utils'

export type Difficulty  = 'easy' | 'medium' | 'hard'
export type SudokuMode  = 'standard' | 'mini'

export const BOX_SIZE: Record<SudokuMode, number> = {
  standard: 3,
  mini:     2,
}

export const GRID_SIZE: Record<SudokuMode, number> = {
  standard: 9,
  mini:     4,
}

// How many cells to pre-fill for each mode × difficulty combination.
// Total cells: 9×9 = 81, 4×4 = 16.
export const GIVEN_COUNTS: Record<SudokuMode, Record<Difficulty, number>> = {
  standard: { easy: 46, medium: 36, hard: 26 },
  mini:     { easy: 12, medium: 9,  hard: 6  },
}

export interface SudokuPuzzle {
  grid:     number[][]   // 0 = empty
  solution: number[][]   // full solved grid
  given:    boolean[][]  // true = locked cell
}

// ── Solver / Generator ─────────────────────────────────────────

function isValidPlacement(
  grid:    number[][],
  r: number, c: number, n: number,
  size: number, boxSize: number,
): boolean {
  for (let col = 0; col < size; col++) {
    if (grid[r][col] === n) return false
  }
  for (let row = 0; row < size; row++) {
    if (grid[row][c] === n) return false
  }
  const br = Math.floor(r / boxSize) * boxSize
  const bc = Math.floor(c / boxSize) * boxSize
  for (let dr = 0; dr < boxSize; dr++) {
    for (let dc = 0; dc < boxSize; dc++) {
      if (grid[br + dr][bc + dc] === n) return false
    }
  }
  return true
}

// Fills grid in-place via backtracking with random candidate ordering.
function fillGrid(grid: number[][], size: number, boxSize: number): boolean {
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (grid[r][c] !== 0) continue
      const candidates = shuffle(Array.from({ length: size }, (_, i) => i + 1))
      for (const n of candidates) {
        if (isValidPlacement(grid, r, c, n, size, boxSize)) {
          grid[r][c] = n
          if (fillGrid(grid, size, boxSize)) return true
          grid[r][c] = 0
        }
      }
      return false
    }
  }
  return true
}

function generateFilledGrid(size: number, boxSize: number): number[][] {
  const grid: number[][] = Array.from({ length: size }, () => Array(size).fill(0))
  fillGrid(grid, size, boxSize)
  return grid
}

// ── Public factory ─────────────────────────────────────────────

export function generatePuzzle(mode: SudokuMode, difficulty: Difficulty): SudokuPuzzle {
  const size    = GRID_SIZE[mode]
  const boxSize = BOX_SIZE[mode]
  const givens  = GIVEN_COUNTS[mode][difficulty]

  const solution = generateFilledGrid(size, boxSize)
  const grid     = solution.map(row => [...row])

  // Remove cells at random until we reach the target given count
  const positions = shuffle(
    Array.from({ length: size }, (_, r) =>
      Array.from({ length: size }, (_, c): [number, number] => [r, c]),
    ).flat(),
  )

  let removed = 0
  const toRemove = size * size - givens
  for (const [r, c] of positions) {
    if (removed >= toRemove) break
    grid[r][c] = 0
    removed++
  }

  const given: boolean[][] = grid.map(row => row.map(v => v !== 0))

  return { grid, solution, given }
}

// ── Helpers used by the hook ───────────────────────────────────

/** Returns boolean[][] where true means the user-entered value is wrong. */
export function computeConflicts(
  grid:     number[][],
  solution: number[][],
  given:    boolean[][],
  size:     number,
): boolean[][] {
  return Array.from({ length: size }, (_, r) =>
    Array.from({ length: size }, (_, c) =>
      !given[r][c] && grid[r][c] !== 0 && grid[r][c] !== solution[r][c],
    ),
  )
}

/** Game is complete when every non-given cell equals the solution. */
export function checkComplete(
  grid:     number[][],
  solution: number[][],
  given:    boolean[][],
  size:     number,
): boolean {
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (!given[r][c] && grid[r][c] !== solution[r][c]) return false
    }
  }
  return true
}

/** Count how many non-given cells have the correct value. */
export function countCorrect(
  grid:     number[][],
  solution: number[][],
  given:    boolean[][],
  size:     number,
): number {
  let n = 0
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (!given[r][c] && grid[r][c] === solution[r][c]) n++
    }
  }
  return n
}

/** Count total empty (non-given) cells. */
export function countEmpty(given: boolean[][], size: number): number {
  let n = 0
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (!given[r][c]) n++
    }
  }
  return n
}
